<script>
import axios from 'axios';
import * as moment from 'moment'

export default {
  data: () => ({
    busyLoading: false,

    rows: [],

    importButtonText: 'Bulk Import Packing Records',
    localImportUrl: localStorage.getItem('bulk-import-layers-packing-url'),

    operations: {
      filtering: [
        { text: `Rows with batch name 'All'`, func: x => x.batch && x.batch.toLowerCase() === 'All'.toLowerCase() },
      ],
      transforms: [
        { text: `Append '-2019' to each batch names`, func: x => { x.batch += '-2019' } },
      ],
    }
  }),

  computed: {
    importUrl: {
      get() { return this.localImportUrl },
      set(value) { this.localImportUrl = value; localStorage.setItem('bulk-import-layers-packing-url', value) },
    },

    hasRows() { return this.rows && this.rows.length > 0 },
  },

  methods: {
    async loadPackingSheet() {
      this.busyLoading = true;
      this.rows = [];

      try {
        let response = await axios.get(this.importUrl);
        this.rows = this.processPackingCsv(response.data.values);
      } finally {
        this.busyLoading = false;
      }
    },

    processPackingCsv(data) {
      let headers = data.splice(0, 1)[0];
      let rows = data;

      let headerNames = {
        Date: 'Date',
        Batch: 'Batch',
        SuperJumbo: 'Super Jumbo',
        Jumbo: 'Jumbo',
        XLarge: 'X-Large',
        Large: 'Large',
        BigMedium: 'Big Medium',
        SmallMedium: 'Small Medium',
        Small: 'Small',
        Broken: 'Broken',
        // Total: 'TOTAL',
        // DailySheetTotal: 'Daily sheet total',
        // Difference: 'Difference',
        // AvgEggWeight: 'Avg Egg weight',
      }

      let headerIndexes = {};
      Object.keys(headerNames).forEach(x => { headerIndexes[headerNames[x]] = headers.indexOf(headerNames[x]) });

      let missingHeadersKeys = Object.keys(headerIndexes).map(x => ({ key: x, index: headerIndexes[x] })).filter(x => x.index === -1).map(x => x.key);
      if (missingHeadersKeys.length > 0) {
        console.error('Missing headers!', missingHeadersKeys);
        alert(`Missing data headers ${missingHeadersKeys.join(', ')}`);
        return [];
      }

      let products = rows
        .map((x, rowIdx) => {
          let dateStr = x[headerIndexes[headerNames.Date]];

          if (!dateStr) {
            // Find a date in the next row, typically this is for merged Excel/GoogleSheet cells
            for (let i = 1; i <= 5; i++) {
              if (rowIdx - i < 0) {
                break;
              }
              let tmpRow = rows[rowIdx - i];
              if (tmpRow[headerIndexes[headerNames.Date]]) {
                dateStr = tmpRow[headerIndexes[headerNames.Date]];
                break;
              }
            }
          }

          if (!dateStr) {
            console.warn('Date not found in row:', x);
            let sheetRowNum = rowIdx + 2; // Our rowIdx is 0-based as well as the headers were removed
            throw new Error(`Unable to find Date for row ${sheetRowNum}. Perhaps it is an empty cell or a merged cell?`);
          }

          return {
            date: moment.utc(dateStr, 'YYYY-MM-DD').format('YYYY-MM-DD'),
            batch: (x[headerIndexes[headerNames.Batch]] || '').trim(),
            sizes: {
              SuperJumbo: x[headerIndexes[headerNames.SuperJumbo]] || 0,
              Jumbo: x[headerIndexes[headerNames.Jumbo]] || 0,
              XLarge: x[headerIndexes[headerNames.XLarge]] || 0,
              Large: x[headerIndexes[headerNames.Large]] || 0,
              BigMedium: x[headerIndexes[headerNames.BigMedium]] || 0,
              SmallMedium: x[headerIndexes[headerNames.SmallMedium]] || 0,
              Small: x[headerIndexes[headerNames.Small]] || 0,
              Broken: x[headerIndexes[headerNames.Broken]] || 0,
            },
            // total: x[headerIndexes[headerNames.Total]] || 0,
            // daily_sheet_total: x[headerIndexes[headerNames.DailySheetTotal]] || 0,
            // difference: x[headerIndexes[headerNames.Difference]] || 0,
            // avg_egg_weight: x[headerIndexes[headerNames.AvgEggWeight]] || 0,
          };
        })
        .filter(x => !this.operations.filtering.find(filterOp => filterOp.func(x)));

      products.forEach(x => { this.operations.transforms.forEach(transformOp => transformOp.func(x)) });

      return products;
    },

    onSuccessfullyImported() {
      alert('Successfully imported');
      this.rows = [];
    },
  },
}
</script>

<template>
  <div>
    <form @submit.prevent="loadPackingSheet">
      <b-input-group>
        <b-form-input v-model="importUrl" />
        <b-input-group-append>
          <b-button type="submit" variant="success" :disabled="!importUrl">
            <b-spinner v-if="busyLoading" small />
            Load Sheet
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </form>

    <div v-if="hasRows">
      <b-alert variant="success" show fade class="mt-3">
        Successfully loaded rows. Double-check data in the table below and click the '{{importButtonText}}' button below.
      </b-alert>

      <div class="table-responsive">
        <table class="table table-bordered table-sm small">
          <thead>
            <th> Date </th>
            <th> Batch </th>
            <th> Super jumbo </th>
            <th> Jumbo </th>
            <th> X large </th>
            <th> Large </th>
            <th> Big medium </th>
            <th> Small medium </th>
            <th> Small </th>
            <th> Broken </th>
            <!-- <th> Total </th> -->
            <!-- <th> Daily sheet total </th> -->
            <!-- <th> Difference </th> -->
            <!-- <th> Avg egg weight </th> -->
          </thead>
          <tr v-for="(row, index) in rows" :key="index">
            <td>{{row.date}}</td>
            <td>{{row.batch}}</td>
            <td>{{row.sizes.SuperJumbo}}</td>
            <td>{{row.sizes.Jumbo}}</td>
            <td>{{row.sizes.XLarge}}</td>
            <td>{{row.sizes.Large}}</td>
            <td>{{row.sizes.BigMedium}}</td>
            <td>{{row.sizes.SmallMedium}}</td>
            <td>{{row.sizes.Small}}</td>
            <td>{{row.sizes.Broken}}</td>
            <!-- <td>{{row.total}}</td> -->
            <!-- <td>{{row.daily_sheet_total}}</td> -->
            <!-- <td>{{row.difference}}</td> -->
            <!-- <td>{{row.avg_egg_weight}}</td> -->
          </tr>
        </table>
      </div>

      <b-alert variant="warning" show>
        <i class="fa fa-warning"></i>
        <span>The following operations were applied to the raw sheet data.</span>

        <div class="h4 mb-0 mt-3">Excluded:</div>
        <ul>
          <li v-for="op in operations.filtering" :key="op.text"> {{op.text}} </li>
        </ul>

        <div class="h4 mb-0 mt-3">Modifications:</div>
        <ul>
          <li v-for="op in operations.transforms" :key="op.text"> {{op.text}} </li>
        </ul>

      </b-alert>

      <confirm-click>
        <api-button
          method="put"
          url="v1/layeradmin/bulk-import-packing-records"
          :payload="{ rows: rows }"
          class="btn btn-success btn-block"
          :disabled="!importUrl"
          @executed="onSuccessfullyImported">
          <i class="fa fa-upload"></i>
          {{importButtonText}} ({{rows.length}} rows)
        </api-button>
      </confirm-click>

    </div>

  </div>
</template>



// WEBPACK FOOTER //
// src/layerAdmin/components/BulkImportPackingRecords.vue